import { useState, useEffect, useCallback } from "react";
import type { SaveMeta, SlotId, SaveLoad } from "../engine/SaveLoad";

const SLOTS: { id: SlotId; label: string }[] = [
  { id: "auto", label: "Autosave" },
  { id: "slot1", label: "Slot 1" },
  { id: "slot2", label: "Slot 2" },
  { id: "slot3", label: "Slot 3" },
  { id: "slot4", label: "Slot 4" },
  { id: "slot5", label: "Slot 5" },
];

interface SaveLoadScreenProps {
  mode: "save" | "load";
  saveLoad: SaveLoad;
  onClose: () => void;
  onLoadComplete?: () => void;
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SaveLoadScreen({
  mode,
  saveLoad,
  onClose,
  onLoadComplete,
}: SaveLoadScreenProps) {
  const [metas, setMetas] = useState<Map<string, SaveMeta>>(new Map());
  const [confirmOverwrite, setConfirmOverwrite] = useState<SlotId | null>(null);

  const refresh = useCallback(async () => {
    const list = await saveLoad.listSlots();
    const map = new Map<string, SaveMeta>();
    for (const m of list) map.set(m.slotId, m);
    setMetas(map);
  }, [saveLoad]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSave = async (slotId: SlotId) => {
    if (metas.has(slotId) && confirmOverwrite !== slotId) {
      setConfirmOverwrite(slotId);
      return;
    }
    setConfirmOverwrite(null);
    await saveLoad.save(slotId);
    await refresh();
  };

  const handleLoad = async (slotId: SlotId) => {
    const ok = await saveLoad.load(slotId);
    if (ok) {
      onClose();
      onLoadComplete?.();
    }
  };

  const handleDelete = async (slotId: SlotId) => {
    await saveLoad.deleteSlot(slotId);
    setConfirmOverwrite(null);
    await refresh();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.92)",
        display: "flex",
        flexDirection: "column",
        zIndex: 100,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem 2rem",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.2rem" }}>
          {mode === "save" ? "Save Game" : "Load Game"}
        </h2>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "6px",
            color: "#eee",
            padding: "0.4rem 1rem",
            cursor: "pointer",
            fontSize: "0.85rem",
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "1rem 2rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
        }}
      >
        {SLOTS.map(({ id, label }) => {
          const meta = metas.get(id);
          const isAuto = id === "auto";
          const canSave = mode === "save" && !isAuto;
          const canLoad = mode === "load" && !!meta;

          const isConfirming = confirmOverwrite === id;

          return (
            <div
              key={id}
              className="theme-panel"
              style={{
                padding: "1rem 1.25rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                ...(isConfirming ? { ["--panel-border" as any]: "1px solid rgba(255, 180, 50, 0.6)" } : {}),
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "0.95rem",
                    fontWeight: "bold",
                    marginBottom: meta ? "0.3rem" : 0,
                  }}
                >
                  {label}
                </div>
                {meta && (
                  <>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        opacity: 0.6,
                        marginBottom: "0.15rem",
                      }}
                    >
                      {formatTimestamp(meta.timestamp)} &mdash;{" "}
                      {meta.sceneName}
                    </div>
                    <div
                      style={{
                        fontSize: "0.85rem",
                        opacity: 0.5,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "320px",
                      }}
                    >
                      {meta.dialoguePreview || "—"}
                    </div>
                  </>
                )}
                {!meta && (
                  <div style={{ fontSize: "0.85rem", opacity: 0.3 }}>
                    Empty
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                {canSave && (
                  <button
                    onClick={() => handleSave(id)}
                    style={{
                      padding: "0.4rem 1rem",
                      background:
                        confirmOverwrite === id ? "#c98520" : "#7ec8e3",
                      border: "none",
                      borderRadius: "6px",
                      color: "#111",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                      fontWeight: "bold",
                    }}
                  >
                    {confirmOverwrite === id ? "Confirm" : "Save"}
                  </button>
                )}
                {canLoad && (
                  <button
                    onClick={() => handleLoad(id)}
                    style={{
                      padding: "0.4rem 1rem",
                      background: "#7ec8e3",
                      border: "none",
                      borderRadius: "6px",
                      color: "#111",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                      fontWeight: "bold",
                    }}
                  >
                    Load
                  </button>
                )}
                {meta && !isAuto && (
                  <button
                    onClick={() => handleDelete(id)}
                    style={{
                      padding: "0.4rem 0.75rem",
                      background: "transparent",
                      border: "1px solid rgba(255,100,100,0.3)",
                      borderRadius: "6px",
                      color: "#e55",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
